import { useState, type ReactNode } from 'react'
import { AlertTriangle } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SearchResults } from '@/features/ticket-admin/SearchResults'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { formatDate } from '@/lib/dates'
import { useCompanySearch } from '@/queries/people'
import { useLinearCustomerLinks, useLinearCustomers, useRemoveLinearCustomerLink, useSaveLinearCustomerLink } from '@/queries/linearCustomers'
import { extractErrorMessage } from '@/queries/ticketMutations'
import type { LinearCustomerLinkRow } from '@/types/linearClient'

const selectClass =
  'h-[var(--etn-control-height-input)] w-full rounded-etn-menu border border-etn-border-input bg-etn-surface px-3 text-etn-control text-etn-text-cell'

type Company = { _id: string; name: string }
type Editing = { link: LinearCustomerLinkRow | null }

function DangerAlert({ children }: { children: ReactNode }) {
  return (
    <div role="alert" className="flex gap-3 rounded-etn-menu bg-etn-danger-subtle px-4 py-3 text-sm text-etn-danger-text">
      <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
      <div className="space-y-1">{children}</div>
    </div>
  )
}

function IdLine({ children }: { children: string }) {
  return <div className="font-mono text-etn-micro text-etn-text-soft">{children}</div>
}

function CompanyPicker({ company, locked = false, onPick }: { company: Company | null; locked?: boolean; onPick: (company: Company | null) => void }) {
  const [query, setQuery] = useState('')
  const debounced = useDebouncedValue(query)
  const companies = useCompanySearch(debounced)

  if (company) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-etn-menu border border-etn-border-input px-3 py-2">
        <div>
          <div className="text-sm text-etn-text">{company.name}</div>
          <IdLine>{company._id}</IdLine>
        </div>
        {!locked && (
          <Button size="sm" variant="ghost" onClick={() => onPick(null)}>
            Change
          </Button>
        )}
      </div>
    )
  }
  return (
    <>
      <Input id="linear-link-company" autoComplete="off" placeholder="Search companies" value={query} onChange={e => setQuery(e.target.value)} />
      <SearchResults
        label="Matching companies"
        term={query}
        items={companies.data}
        isFetching={companies.isFetching || query !== debounced}
        onPick={picked => {
          setQuery('')
          onPick({ _id: picked._id, name: picked.name ?? picked._id })
        }}
        renderItem={picked => <span className="text-etn-text-cell">{picked.name || picked._id}</span>}
      />
    </>
  )
}

function LinkDialog({ link, links, onClose }: { link: LinearCustomerLinkRow | null; links: LinearCustomerLinkRow[]; onClose: () => void }) {
  const [company, setCompany] = useState<Company | null>(link ? { _id: link.companyId, name: link.companyName } : null)
  const [customerId, setCustomerId] = useState(link?.linearCustomerId ?? '')
  const customers = useLinearCustomers(true)
  const save = useSaveLinearCustomerLink()
  const takenBy = new Map(links.filter(other => other._id !== link?._id).map(other => [other.linearCustomerId, other.companyName]))

  async function submit() {
    if (!company || !customerId) return
    try {
      const saved = await save.mutateAsync({ ...(link && { _id: link._id }), companyId: company._id, linearCustomerId: customerId })
      if (saved?.warning) toast.warning(saved.warning)
      else toast.success('Link saved')
      onClose()
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Could not save the link'))
    }
  }

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{link ? 'Edit link' : 'Link a company to a Linear customer'}</DialogTitle>
        <DialogDescription>Choose the Etainabl company first, then its Linear customer.</DialogDescription>
      </DialogHeader>
      {link && (
        <DangerAlert>
          <p className="font-semibold">Changing this link changes which issues belong to {link.companyName}.</p>
          <p>
            {link.companyName}&apos;s Linear Tickets tab and Overview will switch to the new customer&apos;s issues, and the old customer&apos;s issues
            will leave {link.companyName}. New issues created from the help desk will be attached to the new customer.
          </p>
        </DangerAlert>
      )}
      <div className="space-y-4">
        <div className="space-y-1">
          <Label htmlFor="linear-link-company">Etainabl company</Label>
          <CompanyPicker company={company} locked={Boolean(link)} onPick={setCompany} />
        </div>
        <div className="space-y-1">
          <Label htmlFor="linear-link-customer">Linear customer</Label>
          <select
            id="linear-link-customer"
            className={selectClass}
            value={customerId}
            onChange={e => setCustomerId(e.target.value)}
            disabled={!company || customers.isLoading}
          >
            <option value="">{customers.isLoading ? 'Loading customers…' : 'Choose a customer'}</option>
            {(customers.data?.data ?? []).map(customer => (
              <option key={customer.id} value={customer.id} disabled={takenBy.has(customer.id)}>
                {takenBy.has(customer.id) ? `${customer.name} (linked to ${takenBy.get(customer.id)})` : customer.name}
              </option>
            ))}
          </select>
          {customerId && <IdLine>{customerId}</IdLine>}
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={submit} disabled={!company || !customerId || save.isPending}>
          Save
        </Button>
      </DialogFooter>
    </DialogContent>
  )
}

function RemoveDialog({ link, onClose }: { link: LinearCustomerLinkRow; onClose: () => void }) {
  const remove = useRemoveLinearCustomerLink()

  async function confirm() {
    try {
      await remove.mutateAsync(link._id)
      toast.success('Link removed')
      onClose()
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Could not remove the link'))
    }
  }

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Remove this link?</DialogTitle>
        <DialogDescription>
          {link.companyName} and {link.linearCustomerName ?? link.linearCustomerId}
        </DialogDescription>
      </DialogHeader>
      <DangerAlert>
        <p className="font-semibold">{link.companyName} will no longer have any Linear issues on the client hub.</p>
        <p>
          Its Linear Tickets tab will empty, and Create Linear issue will refuse for {link.companyName} until it is linked again.
        </p>
      </DangerAlert>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="destructive" onClick={confirm} disabled={remove.isPending}>
          Remove link
        </Button>
      </DialogFooter>
    </DialogContent>
  )
}

function LinksTable({ links, onEdit, onRemove }: { links: LinearCustomerLinkRow[]; onEdit: (link: LinearCustomerLinkRow) => void; onRemove: (link: LinearCustomerLinkRow) => void }) {
  return (
    <table className="w-full text-left">
      <thead>
        <tr className="border-b border-border text-xs text-muted-foreground">
          <th scope="col" className="pb-2 pr-4 font-normal">
            Etainabl company
          </th>
          <th scope="col" className="pb-2 pr-4 font-normal">
            Linear customer
          </th>
          <th scope="col" className="pb-2 pr-4 font-normal">
            Updated
          </th>
          <th scope="col" className="pb-2 font-normal">
            <span className="sr-only">Actions</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {links.map(link => (
          <tr key={link._id} className="border-b border-border align-top last:border-0">
            <td className="py-2 pr-4 text-sm text-foreground">
              {link.companyName}
              <IdLine>{link.companyId}</IdLine>
            </td>
            <td className="py-2 pr-4 text-sm text-foreground">
              {link.linearCustomerName ?? <span className="text-muted-foreground">Name unavailable</span>}
              <IdLine>{link.linearCustomerId}</IdLine>
            </td>
            <td className="py-2 pr-4 text-xs text-muted-foreground">{formatDate(link.updatedAt)}</td>
            <td className="space-x-2 py-2 text-right whitespace-nowrap">
              <Button size="sm" variant="outline" aria-label={`Edit ${link.companyName}`} onClick={() => onEdit(link)}>
                Edit
              </Button>
              <Button size="sm" variant="outline" aria-label={`Remove ${link.companyName}`} onClick={() => onRemove(link)}>
                Remove
              </Button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export function LinearCustomersView() {
  const { data, isLoading, error } = useLinearCustomerLinks()
  const [editing, setEditing] = useState<Editing | null>(null)
  const [removing, setRemoving] = useState<LinearCustomerLinkRow | null>(null)
  const links = data?.data ?? []

  return (
    <div className="container mx-auto max-w-4xl px-6 pb-6">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div className="space-y-1.5">
            <CardTitle>Linear customers</CardTitle>
            <p className="text-sm text-muted-foreground">
              Each client company links to one Linear customer. The client hub shows the issues carrying that customer, and Create Linear issue
              attaches it.
            </p>
          </div>
          <Button onClick={() => setEditing({ link: null })}>Add</Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
          {error && <p className="text-sm text-destructive">{extractErrorMessage(error, 'Could not load the Linear customer links')}</p>}
          {data?.warning && <p className="text-sm text-etn-warning-text">{data.warning}</p>}
          {data && !links.length && <p className="text-sm text-muted-foreground">No company is linked to a Linear customer yet.</p>}
          {links.length > 0 && <LinksTable links={links} onEdit={link => setEditing({ link })} onRemove={setRemoving} />}
        </CardContent>
      </Card>

      <Dialog open={editing !== null} onOpenChange={open => !open && setEditing(null)}>
        {editing && <LinkDialog key={editing.link?._id ?? 'new'} link={editing.link} links={links} onClose={() => setEditing(null)} />}
      </Dialog>
      <Dialog open={removing !== null} onOpenChange={open => !open && setRemoving(null)}>
        {removing && <RemoveDialog link={removing} onClose={() => setRemoving(null)} />}
      </Dialog>
    </div>
  )
}
